import React, { useState, useEffect, useCallback } from "react";

import openSocket from "../../../services/socket-io";
import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import { connMeta } from "./theme";
import Avatar from "./Avatar";
import { tagChip, chDot, shortTime } from "./decorate";
import {
  TAG_KEYS,
  CH,
  CH_KEYS,
  PRIO_KEYS,
  PRIO,
  fmtBRL,
} from "../../CrmKanban/kanban/tokens";

// Campo de texto genérico (label + input) restilizado por variante.
const Field = ({ V, label, value, placeholder, onCommit }) => {
  const [local, setLocal] = useState(value || "");
  useEffect(() => setLocal(value || ""), [value]);
  return (
    <label
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        fontSize: 10.5,
        fontWeight: 800,
        color: V.sublabel,
      }}
    >
      {label}
      <input
        value={local}
        placeholder={placeholder}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => {
          if (local !== (value || "")) onCommit(local);
        }}
        style={{
          background: V.inputBg,
          border: V.inputBorder,
          borderRadius: V.fieldRadius,
          padding: "6px 10px",
          fontSize: 12,
          fontFamily: "inherit",
          fontWeight: 400,
          color: V.textStrong,
          outline: "none",
        }}
      />
    </label>
  );
};

const SelectField = ({ V, label, value, options, onChange }) => (
  <label
    style={{
      display: "flex",
      flexDirection: "column",
      gap: 3,
      fontSize: 10.5,
      fontWeight: 800,
      color: V.sublabel,
      minWidth: 0,
    }}
  >
    {label}
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      style={{
        background: V.inputBg,
        border: V.inputBorder,
        borderRadius: V.fieldRadius,
        padding: "6px 8px",
        fontSize: 11.5,
        fontWeight: 700,
        fontFamily: "inherit",
        color: V.textStrong,
        cursor: "pointer",
        width: "100%",
      }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  </label>
);

const SectionTitle = ({ V, children, right }) => (
  <div style={{ display: "flex", alignItems: "baseline" }}>
    <span
      style={{
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: ".06em",
        color: V.labelMuted,
      }}
    >
      {children}
    </span>
    {right != null && (
      <>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 11, fontWeight: 800, color: "#2E7D4F" }}>
          {right}
        </span>
      </>
    )}
  </div>
);

// Sidebar CRM (272px). Retrátil. Abas CONTATO/NEGÓCIO/NOTAS. Dados reais do lead.
const CrmSidebar = ({
  V,
  ticket,
  whatsapp,
  attendants,
  queues,
  open,
  onToggle,
  onLeadChange = () => {},
}) => {
  const [lead, setLead] = useState(null);
  const [notes, setNotes] = useState([]);
  const [tab, setTab] = useState("contato");
  const [cfName, setCfName] = useState("");
  const [cfVal, setCfVal] = useState("");
  const [noteDraft, setNoteDraft] = useState("");

  const number = ticket?.contact?.number;
  const contactId = ticket?.contactId || ticket?.contact?.id;

  // Carrega (find-or-create) o lead pelo número quando o ticket muda.
  useEffect(() => {
    if (!number) {
      setLead(null);
      setNotes([]);
      return;
    }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/crm/leads/by-number/${number}`);
        if (alive) {
          setLead(data);
          onLeadChange(data);
        }
      } catch (err) {
        toastError(err);
      }
    })();
    return () => {
      alive = false;
    };
  }, [number]);

  // Notas do lead + socket ao vivo.
  useEffect(() => {
    if (!lead?.id) {
      setNotes([]);
      return undefined;
    }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/crm/leads/${lead.id}/notes`);
        if (alive) setNotes(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      }
    })();

    const socket = openSocket();
    socket.on("crmLeadNote", (data) => {
      if (data.action === "create" && data.leadId === lead.id) {
        setNotes((prev) => [...prev, data.note]);
      }
      if (data.action === "delete") {
        setNotes((prev) => prev.filter((n) => n.id !== data.noteId));
      }
    });
    return () => {
      alive = false;
      socket.disconnect();
    };
  }, [lead?.id]);

  const patchLead = useCallback(
    async (patch) => {
      if (!lead?.id) return;
      const next = { ...lead, ...patch };
      setLead(next);
      onLeadChange(next); // reflete no header/lista (fonte única em index)
      try {
        await api.put(`/crm/leads/${lead.id}`, patch);
      } catch (err) {
        toastError(err);
      }
    },
    [lead, onLeadChange],
  );

  const patchContact = useCallback(
    async (patch) => {
      if (!contactId) return;
      try {
        await api.put(`/contacts/${contactId}`, patch);
      } catch (err) {
        toastError(err);
      }
    },
    [contactId],
  );

  const patchTicket = useCallback(
    async (patch) => {
      if (!ticket?.id) return;
      try {
        await api.put(`/tickets/${ticket.id}`, patch);
      } catch (err) {
        toastError(err);
      }
    },
    [ticket],
  );

  const extraInfo =
    lead?.contact?.extraInfo || ticket?.contact?.extraInfo || [];

  const commitExtra = async (nextExtra) => {
    await patchContact({ extraInfo: nextExtra });
    setLead((prev) =>
      prev
        ? {
            ...prev,
            contact: { ...(prev.contact || {}), extraInfo: nextExtra },
          }
        : prev,
    );
  };

  const addCustomField = async () => {
    const nm = cfName.trim();
    if (!nm) return;
    await commitExtra([...extraInfo, { name: nm, value: cfVal.trim() }]);
    setCfName("");
    setCfVal("");
  };

  const addNote = async () => {
    const body = noteDraft.trim();
    if (!body || !lead?.id) return;
    setNoteDraft("");
    try {
      await api.post(`/crm/leads/${lead.id}/notes`, { body });
    } catch (err) {
      toastError(err);
    }
  };

  const removeNote = async (noteId) => {
    try {
      await api.delete(`/crm/leads/${lead.id}/notes/${noteId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const name = ticket?.contact?.name || ticket?.contact?.number || "Sem nome";
  const phone = ticket?.contact?.number || "";
  const tag = tagChip(lead?.tag);
  const ch = chDot(lead?.channel);
  const conn = connMeta(whatsapp?.status === "CONNECTED");

  // Painel retraído: trilho de 42px.
  if (!open) {
    return (
      <div
        style={{
          width: 42,
          flex: "none",
          background: "#fff",
          borderLeft: V.headerBorder,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          paddingTop: 10,
          gap: 12,
        }}
      >
        <button
          type="button"
          onClick={onToggle}
          title="Expandir painel"
          style={railBtn(V)}
        >
          «
        </button>
        <Avatar
          url={ticket?.contact?.profilePicUrl}
          name={name}
          size={26}
          radius={V.avatarRadius}
          fontSize={9}
        />
        <span
          style={{
            writingMode: "vertical-rl",
            fontSize: 9.5,
            fontWeight: 800,
            letterSpacing: ".08em",
            color: V.labelMuted,
          }}
        >
          DETALHES
        </span>
      </div>
    );
  }

  return (
    <div
      style={{
        width: 272,
        flex: "none",
        background: "#fff",
        borderLeft: V.headerBorder,
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
        minHeight: 0,
        position: "relative",
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        title="Retrair painel"
        style={{
          ...railBtn(V),
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 2,
        }}
      >
        »
      </button>

      {/* Perfil */}
      <div
        style={{
          padding: "18px 16px 14px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 4,
          borderBottom: V.divider,
        }}
      >
        <Avatar
          url={ticket?.contact?.profilePicUrl}
          name={name}
          size={54}
          radius={V.avatarRadius === "50%" ? "50%" : 16}
          fontSize={18}
        />
        <div
          style={{
            fontSize: 14.5,
            fontWeight: 800,
            color: V.textStrong,
            marginTop: 4,
          }}
        >
          {name}
        </div>
        <div style={{ fontSize: 11.5, color: V.phoneText }}>{phone}</div>
        <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
          {ch && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                background: "#F4EFE4",
                color: "#6B6456",
                fontSize: 10,
                fontWeight: 800,
                padding: "2px 9px",
                borderRadius: 999,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: ch.color,
                }}
              />
              {ch.label}
            </span>
          )}
          {tag && (
            <span
              style={{
                background: tag.bg,
                color: tag.color,
                fontSize: 10,
                fontWeight: 800,
                padding: "2px 9px",
                borderRadius: V.tagRadius,
              }}
            >
              {tag.label}
            </span>
          )}
        </div>
      </div>

      {/* Abas */}
      <div style={{ display: "flex", borderBottom: V.divider }}>
        {[
          ["contato", "CONTATO"],
          ["negocio", "NEGÓCIO"],
          ["notas", "NOTAS"],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            style={{
              flex: 1,
              background: "none",
              border: "none",
              borderBottom: `2.5px solid ${tab === key ? V.prim : "transparent"}`,
              color: tab === key ? V.prim : V.mut,
              fontWeight: 800,
              fontSize: 10.5,
              letterSpacing: ".04em",
              padding: "9px 0 8px",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* CONTATO */}
      {tab === "contato" && (
        <>
          <div style={sectionStyle(V)}>
            <SectionTitle V={V}>INFORMAÇÕES DO CONTATO</SectionTitle>
            <Field
              V={V}
              label="Telefone"
              value={phone}
              placeholder="Adicionar telefone"
              onCommit={(v) => patchContact({ number: v })}
            />
            <Field
              V={V}
              label="E-mail"
              value={lead?.email}
              placeholder="Adicionar e-mail"
              onCommit={(v) => {
                patchLead({ email: v });
                patchContact({ email: v });
              }}
            />
            <Field
              V={V}
              label="Empresa"
              value={lead?.empresa}
              placeholder="Adicionar empresa"
              onCommit={(v) => patchLead({ empresa: v })}
            />
            <Field
              V={V}
              label="Cidade"
              value={lead?.cidade}
              placeholder="Adicionar cidade"
              onCommit={(v) => patchLead({ cidade: v })}
            />
          </div>
          <div style={sectionStyle(V)}>
            <SectionTitle V={V}>CAMPOS PERSONALIZADOS</SectionTitle>
            {extraInfo.map((f, i) => (
              <div
                key={`${f.name}-${i}`}
                style={{ display: "flex", flexDirection: "column", gap: 3 }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span
                    style={{
                      fontSize: 10.5,
                      fontWeight: 800,
                      color: V.sublabel,
                      flex: 1,
                    }}
                  >
                    {f.name}
                  </span>
                  <button
                    type="button"
                    title="Remover campo"
                    onClick={() =>
                      commitExtra(extraInfo.filter((_, j) => j !== i))
                    }
                    style={{
                      background: "none",
                      border: "none",
                      color: "#C9C0AD",
                      fontSize: 13,
                      fontWeight: 800,
                      cursor: "pointer",
                      padding: "0 2px",
                      fontFamily: "inherit",
                    }}
                  >
                    ×
                  </button>
                </div>
                <input
                  defaultValue={f.value}
                  onBlur={(e) => {
                    if (e.target.value !== f.value) {
                      commitExtra(
                        extraInfo.map((g, j) =>
                          j === i ? { ...g, value: e.target.value } : g,
                        ),
                      );
                    }
                  }}
                  style={inputStyle(V)}
                />
              </div>
            ))}
            <div style={{ display: "flex", gap: 6 }}>
              <input
                value={cfName}
                onChange={(e) => setCfName(e.target.value)}
                placeholder="Campo"
                style={{ ...inputStyle(V), width: 88, flex: "none" }}
              />
              <input
                value={cfVal}
                onChange={(e) => setCfVal(e.target.value)}
                placeholder="Valor"
                style={{ ...inputStyle(V), flex: 1, minWidth: 0 }}
              />
              <button
                type="button"
                title="Adicionar campo"
                onClick={addCustomField}
                style={{
                  background: "#fff",
                  border: `1.5px solid ${V.prim}`,
                  color: V.prim,
                  borderRadius: V.fieldRadius,
                  padding: "6px 10px",
                  fontWeight: 900,
                  fontSize: 12,
                  fontFamily: "inherit",
                  cursor: "pointer",
                }}
              >
                +
              </button>
            </div>
          </div>
        </>
      )}

      {/* NEGÓCIO */}
      {tab === "negocio" && (
        <>
          <div style={sectionStyle(V)}>
            <SectionTitle V={V} right={fmtBRL(lead?.value)}>
              NEGÓCIO
            </SectionTitle>
            <Field
              V={V}
              label="Valor (R$)"
              value={lead?.value != null ? String(lead.value) : ""}
              placeholder="0"
              onCommit={(v) =>
                patchLead({
                  value: Number(String(v).replace(/[^0-9]/g, "")) || 0,
                })
              }
            />
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
              }}
            >
              <SelectField
                V={V}
                label="Estágio"
                value={lead?.stageId}
                options={(queues.stages || []).map((s) => ({
                  value: s.id,
                  label: s.name,
                }))}
                onChange={(v) => patchLead({ stageId: Number(v) || null })}
              />
              <SelectField
                V={V}
                label="Prioridade"
                value={lead?.priority}
                options={PRIO_KEYS.map((p) => ({
                  value: p,
                  label: PRIO[p][0],
                }))}
                onChange={(v) => patchLead({ priority: v })}
              />
              <SelectField
                V={V}
                label="Tag"
                value={lead?.tag}
                options={TAG_KEYS.map((t) => ({ value: t, label: t }))}
                onChange={(v) => patchLead({ tag: v })}
              />
              <SelectField
                V={V}
                label="Canal"
                value={lead?.channel}
                options={CH_KEYS.map((c) => ({ value: c, label: CH[c][0] }))}
                onChange={(v) => patchLead({ channel: v })}
              />
            </div>
          </div>
          <div style={sectionStyle(V)}>
            <SectionTitle V={V}>ATENDIMENTO</SectionTitle>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span
                style={{
                  fontSize: 10.5,
                  fontWeight: 800,
                  color: V.sublabel,
                  flex: 1,
                }}
              >
                Conexão
              </span>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  fontSize: 10.5,
                  fontWeight: 800,
                  color: conn.col,
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: conn.dot,
                  }}
                />
                {conn.text}
              </span>
            </div>
            <SelectField
              V={V}
              label="Atendente"
              value={ticket?.userId || ""}
              options={[
                { value: "", label: "Sem atendente" },
                ...attendants.map((a) => ({ value: a.id, label: a.name })),
              ]}
              onChange={(v) => patchTicket({ userId: v ? Number(v) : null })}
            />
            <SelectField
              V={V}
              label="Fila"
              value={ticket?.queueId || ""}
              options={[
                { value: "", label: "Sem fila" },
                ...(queues.list || []).map((q) => ({
                  value: q.id,
                  label: q.name,
                })),
              ]}
              onChange={(v) => patchTicket({ queueId: v ? Number(v) : null })}
            />
          </div>
        </>
      )}

      {/* NOTAS */}
      {tab === "notas" && (
        <div style={{ ...sectionStyle(V), borderBottom: "none" }}>
          <SectionTitle V={V}>NOTAS INTERNAS</SectionTitle>
          {notes.map((n) => (
            <div
              key={n.id}
              style={{
                background: V.noteBg,
                border: V.noteBorder,
                borderRadius: V.fieldRadius,
                padding: "7px 9px",
                position: "relative",
              }}
            >
              <div
                style={{
                  fontSize: 11.5,
                  color: V.noteText,
                  lineHeight: 1.4,
                  paddingRight: 14,
                }}
              >
                {n.body}
              </div>
              <div
                style={{
                  fontSize: 9.5,
                  color: V.noteTime,
                  textAlign: "right",
                  marginTop: 2,
                }}
              >
                {n.user?.name ? `${n.user.name} · ` : ""}
                {shortTime(n.createdAt)}
              </div>
              <button
                type="button"
                title="Remover nota"
                onClick={() => removeNote(n.id)}
                style={{
                  position: "absolute",
                  top: 4,
                  right: 6,
                  background: "none",
                  border: "none",
                  color: V.noteTime,
                  fontSize: 12,
                  fontWeight: 800,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                ×
              </button>
            </div>
          ))}
          <div style={{ display: "flex", gap: 6 }}>
            <input
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") addNote();
              }}
              placeholder="Adicionar nota…"
              style={{ ...inputStyle(V), flex: 1, minWidth: 0 }}
            />
            <button
              type="button"
              onClick={addNote}
              style={{
                background: V.prim,
                border: "none",
                color: "#fff",
                borderRadius: V.fieldRadius,
                padding: "6px 12px",
                fontWeight: 900,
                fontSize: 13,
                fontFamily: "inherit",
                cursor: "pointer",
              }}
            >
              +
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const sectionStyle = (V) => ({
  padding: "13px 16px",
  borderBottom: V.divider,
  display: "flex",
  flexDirection: "column",
  gap: 9,
});

const inputStyle = (V) => ({
  background: V.inputBg,
  border: V.inputBorder,
  borderRadius: V.fieldRadius,
  padding: "6px 8px",
  fontSize: 11.5,
  fontFamily: "inherit",
  color: V.textStrong,
  outline: "none",
});

const railBtn = (V) => ({
  background: "#fff",
  border: "1px solid #E8DFCE",
  color: V.labelMuted,
  borderRadius: V.key === "b" ? 7 : 999,
  width: 22,
  height: 22,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 11,
  fontWeight: 800,
  cursor: "pointer",
  fontFamily: "inherit",
});

export default CrmSidebar;
